import { buildRiskRecognitionInvestigatePrompt } from '@cobliteam/ai/src/lib/pageContext';
import { useAIAssistant } from '@cobliteam/ai/src/store/useAIAssistant';
import { Analytics } from '@cobliteam/analytics';
import type { RiskRecognitionHighlight } from '@cobliteam/api-lib/src/dashboard/riskRecognitions/Types';
import { Location } from '@cobliteam/common-types';
import { useRiskRecognitionList } from '@cobliteam/hooks/src/useEntity/useRiskRecognitions';
import { useCreateRiskRecognitionSilence } from '@cobliteam/hooks/src/useEntity/useRiskRecognitions/useCreateRiskRecognitionSilence';
import { useTranslation } from '@cobliteam/i18n';
import Drawer, { DrawerBody, DrawerClose, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from '@cobliteam/ui/components/Drawer';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { parseISO } from '@cobliteam/utils/date';
import { createSerializer, parseAsInteger, parseAsString } from 'nuqs';
import { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import useNotificationDrawerStore from '../../core/store/useNotificationDrawerStore';
import { buildRiskRecognitionEventProps } from './analytics';
import { toDriverDetails, toDriverTitleCase, toInvestigation } from './mockInvestigation';
import { RISK_DRAWER_PARAM } from './parse';
import RiskRecognitionDriverDetails from './RiskRecognitionDriverDetails';
import RiskRecognitionInvestigation from './RiskRecognitionInvestigation';

const RISK_RECOGNITION_TYPE_KEY = 'risk-recognition';

// Resolve o highlight do deep link pelo id, procurando na listagem (1ª página) e populando o store pra o
// drawer renderizar. Se a busca assentar sem achar (id arquivado / fora da 1ª página / removido), limpa o
// param — senão o drawer fica aberto e vazio, preso pelo query param.
const RiskRecognitionDeepLinkLoader = ({ itemId }: { itemId: string }) => {
  const openDrawer = useNotificationDrawerStore((s) => s.openDrawer);
  const history = useHistory();
  const location = useLocation();
  const { items, isSuccess } = useRiskRecognitionList({});

  useEffect(() => {
    const found = items.find((item) => item.id === itemId);
    if (found) {
      openDrawer(RISK_RECOGNITION_TYPE_KEY, found);
      return;
    }
    if (isSuccess) {
      const params = new URLSearchParams(location.search);
      params.delete(RISK_DRAWER_PARAM);
      history.replace({ search: params.toString() });
    }
  }, [items, isSuccess, itemId, openDrawer, history, location.search]);

  return null;
};

// Type-safe builder for the /tracking deep link. `date` is epoch ms, read back as a number on /tracking.
const serializeTrackingLink = createSerializer({
  vehicle_id: parseAsString,
  date: parseAsInteger,
});

/**
 * Drawer do risco (self-contained). Lê o store genérico de drawer: só se considera aberto quando o tipo
 * ativo é o risco (`activeType === 'risk-recognition'`). Mantém o param de deep link legado próprio
 * (`?risk-recognition-drawer=<id>`) pra links já compartilhados continuarem abrindo.
 */
const RiskRecognitionDrawer = () => {
  const { t } = useTranslation('notifications');
  const activeType = useNotificationDrawerStore((s) => s.activeType);
  const isDrawerOpenStore = useNotificationDrawerStore((s) => s.isDrawerOpen);
  const activePayload = useNotificationDrawerStore((s) => s.activePayload);
  const closeDrawer = useNotificationDrawerStore((s) => s.closeDrawer);
  const history = useHistory();
  const location = useLocation();
  const { openAISidepanelWithHiddenInput, resetNewThreadId, setThreadId } = useAIAssistant();
  const { mutate: createSilence } = useCreateRiskRecognitionSilence();

  const isRiskActive = activeType === RISK_RECOGNITION_TYPE_KEY;
  const highlight = isRiskActive ? (activePayload as RiskRecognitionHighlight | null) : null;

  // Soma ao gatilho do store (card/toast usam `openDrawer`); o param vem do deep link / feed do sino e
  // carrega o id do item (`?risk-recognition-drawer=<id>`) pra resolver o highlight via listagem da API.
  const deepLinkItemId = new URLSearchParams(location.search).get(RISK_DRAWER_PARAM);
  const isDrawerParamOpen = !!deepLinkItemId;
  const isOpen = (isDrawerOpenStore && isRiskActive) || isDrawerParamOpen;
  // Resolve o highlight do id só quando precisa: há deep link e ainda sem highlight no store. O
  // `id === 'true'` é o sentinel legado (sem id) — nada a resolver.
  const shouldResolveDeepLink = !highlight && !!deepLinkItemId && deepLinkItemId !== 'true';

  // Fecha o drawer de verdade: baixa a flag do store E tira o param do deep link. Só `closeDrawer()` não
  // basta quando o drawer foi aberto por `?risk-recognition-drawer=<id>` — o param mantém `isOpen` true e
  // o drawer fica aberto atrás do sidepanel. `replace` (sem nova entrada) também evita reabrir no reload.
  const closeDrawerAndClearParam = () => {
    closeDrawer();
    if (isDrawerParamOpen) {
      const params = new URLSearchParams(location.search);
      params.delete(RISK_DRAWER_PARAM);
      history.replace({ search: params.toString() });
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (open) return;
    Analytics.track('AI - Risk Recognition - Drawer Closed', buildRiskRecognitionEventProps(highlight));
    closeDrawerAndClearParam();
  };

  const handleViewRoute = () => {
    Analytics.track('AI - Risk Recognition - View Route Clicked', buildRiskRecognitionEventProps(highlight));
    const vehicleId = highlight?.metadata?.vehicle_id;
    if (!vehicleId) return;
    const locatedAt = highlight?.metadata?.last_location?.located_at;
    // parseISO keeps timezone parsing deterministic across browsers (Safari chokes on raw Date.parse).
    const parsed = locatedAt ? parseISO(locatedAt).getTime() : Number.NaN;
    const date = Number.isNaN(parsed) ? Date.now() : parsed;
    closeDrawer();
    history.push(serializeTrackingLink(Location.TRACKING, { vehicle_id: vehicleId, date }));
  };

  const handleInvestigate = async () => {
    if (!highlight) return;
    Analytics.track('AI - Risk Recognition - Investigate Clicked', buildRiskRecognitionEventProps(highlight));
    Analytics.track('AI - Sidepanel - Opened', {
      source: 'risk-recognition-drawer',
      deviceId: highlight.metadata?.device_id,
      vehicleId: highlight.metadata?.vehicle_id,
    });

    const mainFactorTitle = highlight.risk_factors.find((factor) => factor.type === 'main')?.title ?? highlight.risk_factors[0]?.title;
    const prompt = buildRiskRecognitionInvestigatePrompt({
      driverName: highlight.metadata?.driver_name ? toDriverTitleCase(highlight.metadata.driver_name) : undefined,
      mainFactorTitle,
      driverId: highlight.metadata?.driver_id,
      vehicleId: highlight.metadata?.vehicle_id,
      licensePlate: highlight.metadata?.vehicle_plate,
      deviceId: highlight.metadata?.device_id,
      ruleName: highlight.metadata?.rule_name,
    });

    closeDrawerAndClearParam();
    await setThreadId('');
    resetNewThreadId();
    openAISidepanelWithHiddenInput(prompt);
  };

  // The anchor's `href` handles the WhatsApp navigation; here we record the fleet-wide silence (~1h,
  // contacted) alongside analytics. Fired from both WhatsApp entry points (body link + footer button) —
  // server is idempotent on (detection_id, reason). Guarded: no detection_id → skip the POST, still navigate.
  const handleTalkToDriver = () => {
    const detectionId = highlight?.metadata?.detection_id;
    Analytics.track('AI - Risk Recognition - Talk To Driver Clicked', { ...buildRiskRecognitionEventProps(highlight), silenced: Boolean(detectionId), silenceReason: 'contacted' });
    if (detectionId) createSilence({ detectionId, reason: 'contacted' });
  };

  const driverDetails = highlight ? toDriverDetails(highlight) : null;
  const investigation = highlight ? toInvestigation(highlight) : null;
  const driverWhatsAppUrl = driverDetails?.whatsAppUrl ?? '';

  return (
    <Drawer open={isOpen} onOpenChange={handleOpenChange}>
      {shouldResolveDeepLink && deepLinkItemId && <RiskRecognitionDeepLinkLoader itemId={deepLinkItemId} />}
      <DrawerContent size='md'>
        <DrawerHeader>
          <div className='flex items-center gap-2'>
            <DrawerTitle>{t('risk_recognition.drawer.title')}</DrawerTitle>
          </div>
          <DrawerClose />
        </DrawerHeader>

        <DrawerBody className='min-h-0 flex-1 flex-col gap-3 bg-white pt-6'>
          {driverDetails && <RiskRecognitionDriverDetails data={driverDetails} onViewRoute={handleViewRoute} onTalkToDriver={handleTalkToDriver} />}
          {investigation && <RiskRecognitionInvestigation data={investigation} />}
        </DrawerBody>

        <DrawerFooter className='items-center justify-between'>
          <DrawerClose asChild>
            <button
              type='button'
              className='inline-flex h-8 items-center rounded-md px-2 transition-colors hover:bg-[#dcebfe] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'
            >
              <Typography as='span' variant='text-md-semibold' className='text-[#1c53e6]'>
                {t('risk_recognition.drawer.close')}
              </Typography>
            </button>
          </DrawerClose>

          <div className='flex items-center gap-2'>
            {/* The span wrapper lets the tooltip fire over the disabled button. */}
            {driverWhatsAppUrl ? (
              <a
                href={driverWhatsAppUrl}
                target='_blank'
                rel='noopener noreferrer'
                onClick={handleTalkToDriver}
                className='inline-flex h-9 items-center rounded-lg bg-[#dcebfe] px-4 transition-colors hover:bg-[#cfe2fb] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'
              >
                <Typography as='span' variant='text-md-medium' className='whitespace-nowrap text-[#07074f]'>
                  {t('risk_recognition.drawer.talk_to_driver')}
                </Typography>
              </a>
            ) : (
              <Tooltip content={t('risk_recognition.drawer.no_driver_phone')} position='top'>
                <span className='inline-flex'>
                  <button type='button' disabled className='inline-flex h-9 cursor-not-allowed items-center rounded-lg bg-[#dcebfe] px-4 opacity-50'>
                    <Typography as='span' variant='text-md-medium' className='whitespace-nowrap text-[#07074f]'>
                      {t('risk_recognition.drawer.talk_to_driver')}
                    </Typography>
                  </button>
                </span>
              </Tooltip>
            )}

            <button
              type='button'
              onClick={handleInvestigate}
              className={cn(
                'cursor-pointer [background-image:linear-gradient(248.25deg,#8b5cf6_20%,#0248e9_60%,#001bb8_100%)]',
                'inline-flex h-9 items-center gap-2 rounded-lg px-4 transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'
              )}
            >
              <Icon name='search' size='sm' className='text-[#fafafa]' aria-hidden />
              <Typography as='span' variant='text-md-medium' className='whitespace-nowrap text-[#fafafa]'>
                {t('risk_recognition.drawer.investigate')}
              </Typography>
            </button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default RiskRecognitionDrawer;
